import { BehaviorSubject, Observable } from 'rxjs';

import { ProcessingEngine } from '../../engine.model';
import { OperationDescriptor } from '../../operation.model';
import { ProcessingImage } from '../../processing-image';

/**
 * Hugging Face Transformers.js processing engine.
 * Provides AI-powered computer vision operations: depth estimation, segmentation,
 * object detection, image classification, and super-resolution.
 * Models are downloaded on first use and cached in IndexedDB.
 */
export class TransformersJSEngine implements ProcessingEngine {
  readonly id = 'transformers-js';
  readonly name = 'Transformers.js';
  readonly operations: OperationDescriptor[] = [
    {
      id: 'depth-estimation',
      name: 'Depth Estimation',
      engineId: 'transformers-js',
      category: 'detection',
      description: 'Estimates depth map from a single image (MiDaS/DPT)',
      runsOn: 'client',
      requiresManualRun: true,
      params: [
        {
          id: 'model', label: 'Model', type: 'select', defaultValue: 'Xenova/depth-anything-small-hf',
          options: [
            { label: 'Depth Anything Small', value: 'Xenova/depth-anything-small-hf' },
            { label: 'Depth Anything Base', value: 'Xenova/depth-anything-base-hf' },
          ],
        },
      ],
    },
    {
      id: 'image-segmentation',
      name: 'Image Segmentation',
      engineId: 'transformers-js',
      category: 'segmentation',
      description: 'Segments the image into semantic regions',
      runsOn: 'client',
      requiresManualRun: true,
      params: [
        {
          id: 'model', label: 'Model', type: 'select', defaultValue: 'Xenova/segformer-b0-finetuned-ade-512-512',
          options: [
            { label: 'SegFormer B0 (ADE20K)', value: 'Xenova/segformer-b0-finetuned-ade-512-512' },
            { label: 'SegFormer B1 (ADE20K)', value: 'Xenova/segformer-b1-finetuned-ade-512-512' },
          ],
        },
      ],
    },
    {
      id: 'object-detection',
      name: 'Object Detection',
      engineId: 'transformers-js',
      category: 'detection',
      description: 'Detects objects and draws bounding boxes on the image',
      runsOn: 'client',
      requiresManualRun: true,
      params: [
        {
          id: 'model', label: 'Model', type: 'select', defaultValue: 'Xenova/detr-resnet-50',
          options: [
            { label: 'DETR ResNet-50', value: 'Xenova/detr-resnet-50' },
            { label: 'YOLOv8n', value: 'Xenova/yolov8n' },
          ],
        },
        { id: 'threshold', label: 'Confidence', type: 'number', defaultValue: 0.5, min: 0.1, max: 0.99, step: 0.05 },
      ],
    },
    {
      id: 'image-classification',
      name: 'Image Classification',
      engineId: 'transformers-js',
      category: 'detection',
      description: 'Classifies the image content (overlays label on image)',
      runsOn: 'client',
      requiresManualRun: true,
      params: [
        {
          id: 'model', label: 'Model', type: 'select', defaultValue: 'Xenova/vit-base-patch16-224',
          options: [
            { label: 'ViT Base', value: 'Xenova/vit-base-patch16-224' },
            { label: 'MobileNet V2', value: 'Xenova/mobilenetv2_1.0_224' },
          ],
        },
      ],
    },
    {
      id: 'background-removal',
      name: 'Background Removal',
      engineId: 'transformers-js',
      category: 'segmentation',
      description: 'Removes the background from an image',
      runsOn: 'client',
      requiresManualRun: true,
      params: [
        {
          id: 'model', label: 'Model', type: 'select', defaultValue: 'Xenova/modnet',
          options: [
            { label: 'MODNet', value: 'Xenova/modnet' },
          ],
        },
      ],
    },
    {
      id: 'zero-shot-classification',
      name: 'Zero-Shot Classification',
      engineId: 'transformers-js',
      category: 'detection',
      description: 'Classifies an image against custom candidate labels without training',
      runsOn: 'client',
      requiresManualRun: true,
      params: [
        {
          id: 'model', label: 'Model', type: 'select', defaultValue: 'Xenova/clip-vit-base-patch32',
          options: [
            { label: 'CLIP ViT-B/32', value: 'Xenova/clip-vit-base-patch32' },
            { label: 'CLIP ViT-B/16', value: 'Xenova/clip-vit-base-patch16' },
          ],
        },
        { id: 'candidate_labels', label: 'Labels (comma-separated)', type: 'string', defaultValue: 'cat, dog, car, building, nature' },
      ],
    },
    {
      id: 'sam-auto-segment',
      name: 'SAM Auto-Segment',
      engineId: 'transformers-js',
      category: 'segmentation',
      // TODO (interactive mode): extend to accept point/box prompts from the Plotly diagram
      // regions drawn in the image viewer (plotly.service.ts). The SamProcessor already
      // supports input_points / input_boxes in pixel coords — this mode will let users
      // click regions to segment specific objects rather than running the full grid.
      description: 'Segment Anything Model — automatically segments the whole image by running a grid of point prompts. Interactive prompt mode (via diagram regions) is planned.',
      runsOn: 'client',
      requiresManualRun: true,
      params: [
        {
          id: 'model', label: 'Model', type: 'select', defaultValue: 'Xenova/sam-vit-base',
          tooltip: 'SAM checkpoint. Larger models are more accurate but significantly slower in-browser.',
          options: [
            { label: 'SAM ViT-Base (~375 MB)', value: 'Xenova/sam-vit-base' },
          ],
        },
        { id: 'points_per_side', label: 'Grid density', type: 'number', defaultValue: 8, min: 4, max: 16, step: 1,
          tooltip: 'NxN grid of point prompts across the image. Higher = more masks found, slower.' },
        { id: 'pred_iou_thresh', label: 'IoU threshold', type: 'number', defaultValue: 0.88, min: 0.5, max: 0.99, step: 0.01,
          tooltip: 'Masks with predicted IoU below this value are discarded.' },
      ],
    },
    {
      id: 'super-resolution',
      name: 'Super Resolution',
      engineId: 'transformers-js',
      category: 'enhancement',
      description: 'Upscales an image using AI super-resolution',
      runsOn: 'client',
      requiresManualRun: true,
      params: [
        {
          id: 'model', label: 'Model', type: 'select', defaultValue: 'Xenova/swin2SR-classical-sr-x2-64',
          options: [
            { label: 'Swin2SR x2', value: 'Xenova/swin2SR-classical-sr-x2-64' },
            { label: 'Swin2SR x4', value: 'Xenova/swin2SR-classical-sr-x4-64' },
          ],
        },
      ],
    },
  ];

  private initialized = false;
  /** The dedicated inference worker. Created on first execute(), replaced after cancellation. */
  private _worker: Worker | null = null;
  /** Resolve/reject for the currently in-flight execute() call. */
  private _pendingResolve: ((img: ProcessingImage) => void) | null = null;
  private _pendingReject:  ((err: Error)           => void) | null = null;
  /** Monotonic counter used to correlate requests with responses. */
  private _nextId = 0;
  private _inflightId: number | null = null;

  /** Human-readable phase string posted by the worker. `null` when idle.
   *  Mirrors the CellposeEngine.status$ contract so the pipeline dialog can
   *  subscribe to both engines and render the message next to the spinner. */
  private _status = new BehaviorSubject<string | null>(null);
  readonly status$: Observable<string | null> = this._status.asObservable();

  isInitialized(): boolean {
    return this.initialized;
  }

  async initialize(): Promise<void> {
    if (this.initialized) return;
    this.initialized = true;
    // Worker is created lazily on first execute() — nothing else to set up here.
  }

  cancelCurrentOperation(): void {
    if (!this._worker) return;
    // Terminating the worker immediately stops any ONNX computation in progress.
    this._worker.terminate();
    this._worker = null;
    this._pendingReject?.(new DOMException('Operation cancelled by user', 'AbortError'));
    this._pendingResolve = null;
    this._pendingReject  = null;
    this._inflightId     = null;
    this._status.next(null);
  }

  async execute(
    operation: OperationDescriptor,
    input: ProcessingImage,
    params: Record<string, any>
  ): Promise<ProcessingImage> {
    if (!this.initialized) await this.initialize();

    const worker = this.ensureWorker();
    const id = this._nextId++;
    this._inflightId = id;

    // Copy the pixel buffer — we transfer the copy to the worker (zero-copy),
    // keeping the original ProcessingImage intact for caching in the executor.
    const bufCopy = input.data.buffer.slice(0);

    return new Promise<ProcessingImage>((resolve, reject) => {
      this._pendingResolve = resolve;
      this._pendingReject  = reject;

      worker.postMessage(
        { id, operationId: operation.id, imageData: bufCopy, width: input.width, height: input.height,
          channels: input.channels, params },
        [bufCopy] as any
      );
    });
  }

  dispose(): void {
    this.cancelCurrentOperation();
    // Worker is already terminated by cancelCurrentOperation; nothing else to release.
  }

  // ── Private helpers ────────────────────────────────────────────────────────

  private ensureWorker(): Worker {
    if (this._worker) return this._worker;

    const worker = new Worker(
      new URL('./transformers.worker', import.meta.url),
      { type: 'module' }
    );

    worker.onmessage = ({ data }) => {
      if (data.id !== this._inflightId) return; // stale message — ignore

      // Status updates are out-of-band: don't settle the promise, just push
      // the message to subscribers. The worker posts these at each phase
      // (model load, embeddings, mask decode, etc.) so the dialog can show
      // a meaningful spinner caption instead of an opaque "Processing…".
      if (data.type === 'status') {
        this._status.next(data.message);
        return;
      }

      const resolve = this._pendingResolve;
      const reject  = this._pendingReject;
      this._pendingResolve = null;
      this._pendingReject  = null;
      this._inflightId     = null;
      this._status.next(null);

      if (data.error) {
        reject?.(new Error(data.error));
      } else {
        const imgData = new Uint8ClampedArray(data.imageData as ArrayBuffer);
        resolve?.(new ProcessingImage(data.width, data.height, data.channels, imgData));
      }
    };

    worker.onerror = (evt) => {
      const reject = this._pendingReject;
      this._pendingResolve = null;
      this._pendingReject  = null;
      this._inflightId     = null;
      this._status.next(null);
      reject?.(new Error(evt.message || 'Worker error'));
    };

    this._worker = worker;
    return worker;
  }
}
